/**
 * Runtime brand config: validation + merge.
 *
 * A brand may point at an operator-managed JSON document
 * (`Brand.brandConfigUrl`, served by that deployment's pds-pro as
 * `GET /api/brand/<id>.json`) so a district can retheme its client and
 * re-curate the default feeds WITHOUT a rebuild + redeploy.
 *
 * This module is deliberately PURE — no fetch, no storage, no logger, no React
 * — so it can be imported at module-init time by `#/lib/brand` with zero
 * side effects and unit-tested in isolation. The impure halves live in
 * `#/lib/brand/cache` (persistence) and `#/lib/brand/config` (network).
 *
 * ## Threat model
 *
 * The document is UNTRUSTED input from the network. Two rules follow:
 *
 * 1. Only PRESENTATION is overridable. `siteUrl`, `defaultService`, `apiBase`,
 *    the SSO paths and the brand mark are NEVER read from the document — a
 *    hostile value there could break or hijack login. The merge below simply
 *    has no branch that can write them.
 * 2. Every field is shape/format checked before use, and anything that fails
 *    is DROPPED (falling back to the compiled value) rather than applied. A
 *    junk document degrades to "no override", never to a broken client.
 *
 * ## Empty vs. absent
 *
 * The server emits the theme scalars `omitempty`, so a blank/absent field
 * means "no override, keep the compiled value" — it can never blank out a
 * name or a colour. `feeds: null` likewise means "no override, keep the
 * compiled-in brand feeds", which is NOT the same as an empty feed list.
 */

import {
  type Brand,
  type BrandFeedRef,
  type BrandFeeds,
  type BrandSavedFeed,
} from '#/lib/brand/types'

/** Overridable subset of `Brand`. Every field optional = "no override". */
export type BrandRuntimeTheme = {
  name?: string
  appName?: string
  accent?: string
  accentBright?: string
}

/**
 * Feed override. `undefined` for a sub-field means "no override"; note that
 * `discoverFeed: null` IS an override (an explicit opt-out of the discover
 * fallback), which is why the two are distinguished by key presence.
 */
export type BrandRuntimeFeeds = {
  defaultSavedFeeds?: BrandSavedFeed[]
  discoverFeed?: BrandFeedRef | null
}

/** A validated, normalized runtime config. Only ever produced by `validateBrandConfig`. */
export type BrandRuntimeConfig = {
  /** Brand id the document was issued for; must match the compiled brand. */
  id: string
  theme: BrandRuntimeTheme
  /** `null` = no feed override at all. */
  feeds: BrandRuntimeFeeds | null
  updatedAt?: string
}

/* -------------------------------------------------------------------------
 * Limits + primitive validators (mirrors pds-pro `internal/brand/brand.go`)
 * ---------------------------------------------------------------------- */

/** Longest accepted display name / label. */
const MAX_TEXT = 64
/** Longest accepted AT-URI. */
const MAX_URI = 512
/** Hard cap on `defaultSavedFeeds`, so a runaway document can't flood prefs. */
const MAX_SAVED_FEEDS = 32

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

const CONTROL_CHAR_RE = /[\u0000-\u001f\u007f]/
const BRAND_ID_RE = /^[a-z0-9][a-z0-9-]{0,31}$/
/** `at://<did>/<collection>/<rkey>` — DID-addressed only, like the server. */
const AT_URI_RE =
  /^at:\/\/(did:[a-zA-Z0-9._:%-]{1,256})\/([a-zA-Z][a-zA-Z0-9.-]{0,127})\/([A-Za-z0-9._~:-]{1,512})$/

const FEED_COLLECTION = 'app.bsky.feed.generator'
const LIST_COLLECTION = 'app.bsky.graph.list'

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Trimmed string, or undefined if absent/blank/too long/control chars. */
function text(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const s = v.trim()
  if (!s || s.length > MAX_TEXT || CONTROL_CHAR_RE.test(s)) return undefined
  return s
}

/** Lowercased hex colour, or undefined. */
function hexColor(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const s = v.trim()
  if (!HEX_COLOR_RE.test(s)) return undefined
  return s.toLowerCase()
}

/**
 * An `at://` URI addressing `collection`. The collection is enforced because
 * the client picks BOTH its fetch method (`getFeed` vs `getListFeed`) and its
 * feed-descriptor prefix (`feedgen|` vs `list|`) from the entry's type — a
 * list URI filed as a feed silently 404s, which is exactly the "junk applied"
 * case this must reject.
 */
function atUri(v: unknown, collection: string): string | undefined {
  if (typeof v !== 'string') return undefined
  const s = v.trim()
  if (!s || s.length > MAX_URI) return undefined
  const m = AT_URI_RE.exec(s)
  if (!m || m[2] !== collection) return undefined
  return s
}

/* -------------------------------------------------------------------------
 * Document validation
 * ---------------------------------------------------------------------- */

function validateTheme(input: unknown): BrandRuntimeTheme {
  const out: BrandRuntimeTheme = {}
  if (!isPlainObject(input)) return out
  const name = text(input.name)
  if (name) out.name = name
  const appName = text(input.appName)
  if (appName) out.appName = appName
  const accent = hexColor(input.accent)
  if (accent) out.accent = accent
  const accentBright = hexColor(input.accentBright)
  if (accentBright) out.accentBright = accentBright
  return out
}

function validateSavedFeed(input: unknown): BrandSavedFeed | undefined {
  if (!isPlainObject(input)) return undefined
  const type = typeof input.type === 'string' ? input.type.trim() : ''
  // `pinned` must be a real boolean; absent is tolerated as false (unpinned
  // but saved), anything else is junk.
  if (input.pinned !== undefined && typeof input.pinned !== 'boolean') {
    return undefined
  }
  const pinned = input.pinned === true
  const rawValue = typeof input.value === 'string' ? input.value.trim() : ''
  switch (type) {
    case 'timeline':
      // The only timeline is the Following feed.
      if (rawValue && rawValue !== 'following') return undefined
      return {type: 'timeline', value: 'following', pinned}
    case 'feed': {
      const uri = atUri(rawValue, FEED_COLLECTION)
      return uri ? {type: 'feed', value: uri, pinned} : undefined
    }
    case 'list': {
      const uri = atUri(rawValue, LIST_COLLECTION)
      return uri ? {type: 'list', value: uri, pinned} : undefined
    }
    default:
      return undefined
  }
}

function validateFeedRef(input: unknown): BrandFeedRef | undefined {
  if (!isPlainObject(input)) return undefined
  const kind = typeof input.kind === 'string' ? input.kind.trim() : ''
  if (kind !== 'feed' && kind !== 'list') return undefined
  const uri = atUri(
    input.uri,
    kind === 'list' ? LIST_COLLECTION : FEED_COLLECTION,
  )
  if (!uri) return undefined
  const label = text(input.label)
  if (!label) return undefined
  return {kind, uri, label}
}

/**
 * `feeds` from the document.
 *
 * Returns `null` for "no override" — which covers `feeds: null`, `feeds`
 * absent, `feeds` not an object, AND a `feeds` object whose every field was
 * junk. Sub-fields are independent: a document that only sets the discover
 * slot leaves `defaultSavedFeeds` compiled, and vice versa.
 */
function validateFeeds(input: unknown): BrandRuntimeFeeds | null {
  if (!isPlainObject(input)) return null

  const out: BrandRuntimeFeeds = {}

  if (Array.isArray(input.defaultSavedFeeds)) {
    const seen = new Set<string>()
    const feeds: BrandSavedFeed[] = []
    for (const entry of input.defaultSavedFeeds) {
      if (feeds.length >= MAX_SAVED_FEEDS) break
      const feed = validateSavedFeed(entry)
      if (!feed) continue
      const key = `${feed.type}|${feed.value}`
      if (seen.has(key)) continue
      seen.add(key)
      feeds.push(feed)
    }
    /*
     * An EMPTY result is never applied. Either the operator's list is
     * mid-edit/empty, or every entry failed validation; in both cases pinning
     * nothing would leave every new account with no feeds at all. The
     * compiled defaults are strictly safer, so we keep them.
     */
    if (feeds.length > 0) out.defaultSavedFeeds = feeds
  }

  if ('discoverFeed' in input) {
    if (input.discoverFeed === null) {
      // Explicit opt-out of the discover fallback — a real, honoured override.
      out.discoverFeed = null
    } else {
      const ref = validateFeedRef(input.discoverFeed)
      // Junk is dropped (key omitted => keep the compiled discover feed).
      if (ref) out.discoverFeed = ref
    }
  }

  if (out.defaultSavedFeeds === undefined && !('discoverFeed' in out)) {
    return null
  }
  return out
}

/**
 * Validate an untrusted brand document (parsed JSON, or a value read back out
 * of the cache).
 *
 * @param input parsed JSON — any shape, including `undefined`
 * @param expectedId the compiled brand's id; a document issued for a
 *   different brand is rejected outright rather than partially applied
 * @returns a normalized config, or `null` if the document is unusable
 */
export function validateBrandConfig(
  input: unknown,
  expectedId: string,
): BrandRuntimeConfig | null {
  if (!isPlainObject(input)) return null
  if (typeof input.id !== 'string') return null
  const id = input.id.trim()
  if (!BRAND_ID_RE.test(id) || id !== expectedId) return null

  const config: BrandRuntimeConfig = {
    id,
    theme: validateTheme(input.theme),
    feeds: validateFeeds(input.feeds),
  }
  const updatedAt = typeof input.updatedAt === 'string' ? input.updatedAt : ''
  if (updatedAt && updatedAt.length <= 64 && !CONTROL_CHAR_RE.test(updatedAt)) {
    config.updatedAt = updatedAt
  }
  return config
}

/** True when the config carries nothing that would change the compiled brand. */
export function isEmptyBrandConfig(config: BrandRuntimeConfig | null): boolean {
  if (!config) return true
  const {theme, feeds} = config
  return (
    !theme.name &&
    !theme.appName &&
    !theme.accent &&
    !theme.accentBright &&
    !feeds
  )
}

/* -------------------------------------------------------------------------
 * Merge
 * ---------------------------------------------------------------------- */

/**
 * Overlay a validated runtime config on the compiled brand.
 *
 * Returns the SAME object reference when there is nothing to apply, so brands
 * with no `brandConfigUrl` (cooey / eagledrive / yeet) are byte-identical to
 * before — including object identity, in case anything memoizes on it.
 *
 * Only presentation is merged. There is deliberately no branch here that can
 * write `siteUrl`, `defaultService`, `apiBase`, `ssoLoginUrl`,
 * `ssoRedeemPath`, `sso`, `scheme` or the mark geometry.
 */
export function mergeBrandConfig(
  base: Brand,
  config: BrandRuntimeConfig | null,
): Brand {
  if (!config || config.id !== base.id || isEmptyBrandConfig(config)) {
    return base
  }

  const merged: Brand = {...base}
  const {theme} = config
  if (theme.name) merged.name = theme.name
  if (theme.appName) merged.appName = theme.appName
  if (theme.accent) merged.accent = theme.accent
  if (theme.accentBright) merged.accentBright = theme.accentBright

  if (config.feeds) {
    const defaultSavedFeeds =
      config.feeds.defaultSavedFeeds ?? base.feeds?.defaultSavedFeeds
    /*
     * `defaultSavedFeeds` is required by `BrandFeeds`. A brand with no
     * compiled `feeds` (cooey/eagledrive) inherits upstream Bluesky's global
     * defaults from `#/lib/constants`; without a runtime list to install we
     * must NOT synthesize a `feeds` object, or we'd silently drop those.
     */
    if (defaultSavedFeeds) {
      const discoverFeed =
        'discoverFeed' in config.feeds
          ? (config.feeds.discoverFeed ?? null)
          : (base.feeds?.discoverFeed ?? null)
      const feeds: BrandFeeds = {defaultSavedFeeds, discoverFeed}
      merged.feeds = feeds
    }
  }

  return merged
}
